import { CommonModel } from '@telialt/models/src/CommonModel';

import { datadogLogger } from '../datadog/datadogLogger';
import { DDComponent } from '../datadog/setup';
import { getCartSession } from '../eshopUtils';
import { getHybrisSegment } from '../helpers';
import { TeliaSegment } from '../segment';
import { getIsUserAuthorized } from '../SSO/utils';
import { getEshopPasetoToken, getPasetoToken, getToken } from './api';
import { B2B_AGREEMENT_ID_PLACEHOLDER, B2B_AGREEMENT_ID_STORAGE_KEY } from './constants';
import { AuthToken } from './model';

const STORAGE_USER_AUTH_TOKEN_KEY = 'userAuthToken';
const STORAGE_B2B_ACCOUNT_NUMBER_KEY = 'B2B_ACCOUNT_NUMBER';
const STORAGE_B2B_AGREEMENT_ID_KEY = 'B2B_AGREEMENT_ID';
const STORAGE_PASETO_AUTH_TOKEN_KEY = 'pasetoAuthToken';
const STORAGE_PASETO_AUTH_TOKEN_KEY_ESHOP = 'pasetoAuthTokenEshop';
const STORAGE_PASETO_AUTH_TOKEN_KEY_SIM_CARD_MANAGEMENT = 'simCardManagementPasetoAuthToken';
const STORAGE_PASETO_AUTH_TOKEN_KEY_PLAN_CHANGE = 'planChangePasetoAuthToken';

let tokenFetchPromise: Promise<AuthToken> | null = null;
let pasetoTokenFetchPromise: Promise<AuthToken> | null = null;
let pasetoTokenFetchPromiseB2b: Promise<AuthToken> | null = null;
let eshopPasetoTokenFetchPromise: Promise<AuthToken> | null = null;

export const getAuthToken = (force?: boolean): Promise<AuthToken> => {
    const storageUserAuthToken = sessionStorage.getItem(STORAGE_USER_AUTH_TOKEN_KEY);
    const userAuthToken: AuthToken = storageUserAuthToken ? JSON.parse(storageUserAuthToken) : undefined;

    const getIsTokenExpired = () => {
        return (
            !userAuthToken ||
            userAuthToken.expirationTimestamp <= Date.now() ||
            userAuthToken.isAuthorized !== getIsUserAuthorized()
        );
    };

    if (!getIsTokenExpired() && !force) {
        if (!userAuthToken.accessToken) {
            const datadogLogs = datadogLogger();
            datadogLogs.warn('Using invalid eshop token from session storage', {
                data: userAuthToken,
                component: DDComponent.AUTH,
            });
        }

        return Promise.resolve(userAuthToken);
    } else {
        if (force || getIsTokenExpired()) {
            tokenFetchPromise = null;
        }
        if (tokenFetchPromise) {
            return tokenFetchPromise;
        }

        tokenFetchPromise = getToken()
            .then(({ data, status }) => {
                const { access_token, token_type, expires_in } = data;

                if (!access_token || !token_type || !expires_in) {
                    const datadogLogs = datadogLogger();
                    datadogLogs.warn('Received invalid eshop token response', {
                        data,
                        status,
                        component: DDComponent.AUTH,
                    });
                }

                if (expires_in < 30) {
                    const datadogLogs = datadogLogger();
                    datadogLogs.warn('Received eshop token with less than 30 sec expire time', {
                        data,
                        status,
                        component: DDComponent.AUTH,
                    });
                }

                const authToken: AuthToken = {
                    accessToken: access_token,
                    tokenType: token_type,
                    expirationTimestamp: Date.now() + expires_in * 1000,
                    isAuthorized: getIsUserAuthorized(),
                };

                sessionStorage.setItem(STORAGE_USER_AUTH_TOKEN_KEY, JSON.stringify(authToken));
                tokenFetchPromise = null;

                return authToken;
            })
            .catch((error) => {
                const datadogLogs = datadogLogger();
                datadogLogs.warn('Eshop token request failed', { error, component: DDComponent.AUTH });

                throw error;
            });

        return tokenFetchPromise;
    }
};

export const getB2BAgreementId = () => {
    const agreementId = window.sessionStorage.getItem(B2B_AGREEMENT_ID_STORAGE_KEY) || undefined;

    return agreementId !== B2B_AGREEMENT_ID_PLACEHOLDER ? agreementId : undefined;
};

const getPasetoTokenStorageKey = (segment: CommonModel.Segment, agreementId = '') => {
    return [STORAGE_PASETO_AUTH_TOKEN_KEY, segment, agreementId].filter(Boolean).join('_');
};

export const getPasetoAuthToken = (force?: boolean): Promise<AuthToken> => {
    const segment = getHybrisSegment();
    const agreementId = getHybrisSegment() === 'b2b' ? getB2BAgreementId() : undefined;
    const storageKey = getPasetoTokenStorageKey(segment, agreementId);

    const storageUserAuthToken = sessionStorage.getItem(storageKey);
    const userAuthToken: AuthToken = storageUserAuthToken ? JSON.parse(storageUserAuthToken) : undefined;

    if (userAuthToken?.expirationTimestamp > Date.now() && !force) {
        if (!userAuthToken.accessToken) {
            const datadogLogs = datadogLogger();
            datadogLogs.warn('Using invalid Paseto token from session storage', {
                data: userAuthToken,
                component: DDComponent.AUTH,
            });
        }

        return Promise.resolve(userAuthToken);
    } else {
        if (force || (userAuthToken && userAuthToken.expirationTimestamp <= Date.now())) {
            pasetoTokenFetchPromise = null;
        }
        if (pasetoTokenFetchPromise) {
            return pasetoTokenFetchPromise;
        }
        pasetoTokenFetchPromise = getPasetoToken(agreementId)
            .then(({ data, status }) => {
                const { access_token, access_token_id, expires_in } = data;

                if (!access_token || !access_token_id || !expires_in) {
                    const datadogLogs = datadogLogger();
                    datadogLogs.warn('Received invalid Paseto token response', {
                        segment,
                        agreementId,
                        data,
                        status,
                        component: DDComponent.AUTH,
                    });
                }

                if (expires_in < 30) {
                    const datadogLogs = datadogLogger();
                    datadogLogs.warn('Received Paseto token with less than 30 sec expire time', {
                        segment,
                        agreementId,
                        data,
                        status,
                        component: DDComponent.AUTH,
                    });
                }

                const authToken: AuthToken = {
                    accessToken: access_token,
                    accessTokenId: access_token_id,
                    expirationTimestamp: Date.now() + expires_in * 1000,
                };

                sessionStorage.setItem(storageKey, JSON.stringify(authToken));
                pasetoTokenFetchPromise = null;

                return authToken;
            })
            .catch((error) => {
                const datadogLogs = datadogLogger();
                datadogLogs.warn('Paseto token request failed', { error, component: DDComponent.AUTH });

                throw error;
            });

        return pasetoTokenFetchPromise;
    }
};

export const getPasetoAuthTokenB2B = (agreementId: string, force?: boolean): Promise<AuthToken> => {
    const storageKey = getPasetoTokenStorageKey(TeliaSegment.B2b, agreementId);
    const storageUserAuthToken = sessionStorage.getItem(storageKey);
    const userAuthToken: AuthToken = storageUserAuthToken ? JSON.parse(storageUserAuthToken) : undefined;

    if (userAuthToken?.expirationTimestamp > Date.now() && !force) {
        if (!userAuthToken.accessToken) {
            const datadogLogs = datadogLogger();
            datadogLogs.warn('Using invalid Paseto token from session storage', {
                data: userAuthToken,
                component: DDComponent.AUTH,
            });
        }

        return Promise.resolve(userAuthToken);
    } else {
        if (force || (userAuthToken && userAuthToken.expirationTimestamp <= Date.now())) {
            pasetoTokenFetchPromiseB2b = null;
        }
        if (pasetoTokenFetchPromiseB2b) {
            return pasetoTokenFetchPromiseB2b;
        }
        pasetoTokenFetchPromiseB2b = getPasetoToken(agreementId)
            .then(({ data, status }) => {
                const { access_token, access_token_id, expires_in } = data;

                if (!access_token || !access_token_id || !expires_in) {
                    const datadogLogs = datadogLogger();
                    datadogLogs.warn('Received invalid Paseto token response', {
                        segment: TeliaSegment.B2b,
                        agreementId,
                        data,
                        status,
                        component: DDComponent.AUTH,
                    });
                }

                if (expires_in < 30) {
                    const datadogLogs = datadogLogger();
                    datadogLogs.warn('Received Paseto token with less than 30 sec expire time', {
                        segment: TeliaSegment.B2b,
                        agreementId,
                        data,
                        status,
                        component: DDComponent.AUTH,
                    });
                }

                const authToken: AuthToken = {
                    accessToken: access_token,
                    accessTokenId: access_token_id,
                    expirationTimestamp: Date.now() + expires_in * 1000,
                };

                sessionStorage.setItem(storageKey, JSON.stringify(authToken));
                pasetoTokenFetchPromiseB2b = null;

                return authToken;
            })
            .catch((error) => {
                const datadogLogs = datadogLogger();
                datadogLogs.warn('Paseto token request failed', { error, component: DDComponent.AUTH });

                throw error;
            });

        return pasetoTokenFetchPromiseB2b;
    }
};

export const getCartGuid = () => {
    return getCartSession()?.guid;
};

const getEshopPasetoTokenStorageKey = (segment: CommonModel.Segment, cartGuid = '') => {
    return [STORAGE_PASETO_AUTH_TOKEN_KEY_ESHOP, segment, cartGuid].filter(Boolean).join('_');
};

export const getEshopPasetoAuthToken = (force?: boolean): Promise<AuthToken> => {
    const segment = getHybrisSegment();
    const cartGuid = getCartGuid();
    const storageKey = getEshopPasetoTokenStorageKey(segment, cartGuid);
    const datadogLogs = datadogLogger();

    if (!cartGuid) {
        datadogLogs.warn('Requesting anonymous Paseto without eshop cart guid', {
            segment,
            component: DDComponent.AUTH,
        });
        throw new Error('cartGuid is missing for anonymous Paseto');
    }

    const storageEshopPasetoToken = sessionStorage.getItem(storageKey);
    const eshopPasetoToken: AuthToken = storageEshopPasetoToken ? JSON.parse(storageEshopPasetoToken) : undefined;

    if (eshopPasetoToken?.expirationTimestamp > Date.now() && !force) {
        if (!eshopPasetoToken.accessToken) {
            datadogLogs.warn('Using invalid Eshop Paseto token from session storage', {
                data: eshopPasetoToken,
                component: DDComponent.AUTH,
            });
        }

        return Promise.resolve(eshopPasetoToken);
    } else {
        if (force || (eshopPasetoToken && eshopPasetoToken.expirationTimestamp <= Date.now())) {
            eshopPasetoTokenFetchPromise = null;
        }
        if (eshopPasetoTokenFetchPromise) {
            return eshopPasetoTokenFetchPromise;
        }

        const tokenPromise = getIsUserAuthorized() ? getPasetoToken() : getEshopPasetoToken(cartGuid, segment);

        eshopPasetoTokenFetchPromise = tokenPromise
            .then(({ data, status }) => {
                const { access_token, access_token_id, expires_in } = data;

                if (!access_token || !access_token_id || !expires_in) {
                    datadogLogs.warn('Received invalid Eshop Paseto token response', {
                        component: DDComponent.AUTH,
                        segment,
                        cartGuid,
                        data,
                        status,
                    });
                }

                if (expires_in < 30) {
                    datadogLogs.warn('Received Eshop Paseto token with less than 30 sec expire time', {
                        segment,
                        cartGuid,
                        data,
                        status,
                        component: DDComponent.AUTH,
                    });
                }

                const authToken: AuthToken = {
                    accessToken: access_token,
                    accessTokenId: access_token_id,
                    expirationTimestamp: Date.now() + expires_in * 1000,
                };

                sessionStorage.setItem(storageKey, JSON.stringify(authToken));
                eshopPasetoTokenFetchPromise = null;

                return authToken;
            })
            .catch((error) => {
                datadogLogs.warn('Paseto token request failed', { error, component: DDComponent.AUTH });

                throw error;
            });

        return eshopPasetoTokenFetchPromise;
    }
};

export const clearAuthToken = (): void => {
    sessionStorage.removeItem(STORAGE_USER_AUTH_TOKEN_KEY);
    sessionStorage.removeItem(STORAGE_B2B_AGREEMENT_ID_KEY);
    sessionStorage.removeItem(STORAGE_B2B_ACCOUNT_NUMBER_KEY);
    localStorage.removeItem('product-comparison');

    Object.keys(window.sessionStorage).forEach((key) => {
        if (key.startsWith(STORAGE_PASETO_AUTH_TOKEN_KEY_PLAN_CHANGE)) {
            sessionStorage.removeItem(key);
        }
    });

    Object.keys(window.sessionStorage).forEach((key) => {
        if (key.startsWith(STORAGE_PASETO_AUTH_TOKEN_KEY)) {
            sessionStorage.removeItem(key);
        }
    });

    Object.keys(window.sessionStorage).forEach((key) => {
        if (key.startsWith(STORAGE_PASETO_AUTH_TOKEN_KEY_ESHOP)) {
            sessionStorage.removeItem(key);
        }
    });

    Object.keys(window.sessionStorage).forEach((key) => {
        if (key.startsWith(STORAGE_PASETO_AUTH_TOKEN_KEY_SIM_CARD_MANAGEMENT)) {
            sessionStorage.removeItem(key);
        }
    });
};
